// Replay policy — Sprint 3.
//
// Rules (in this order):
//   1. Replay requires consent.allows("replay") === true.
//   2. Route must NOT match the deny list (privileged / payment-sensitive).
//   3. Route must match the allow list OR an "error-triggered" override is set.
//   4. Pass a deterministic per-session sample (default 2%).
//
// Sample rate is intentionally conservative until the masking audit is signed
// off (see Sprint 3 hardening checklist).

import { CONSENT } from "./config";

export const REPLAY = {
  sample_rate: 0.02, // 2% — bump only after manual frame-by-frame audit
  storage_session_decision_key: "nr_replay_decision_v1",
  storage_error_override_key: "nr_replay_error_override_v1",
} as const;

// Routes ALLOWED to be recorded (only when all gates pass).
// Stored as path *prefixes*. Order doesn't matter — first match wins.
const ALLOW_PREFIXES = [
  "/comprar-",
  "/curtidas-",
  "/seguidores-",
  "/painel-",
  "/alternativa-",
  "/impulsionar-",
  "/buy-",
  "/smm-",
  "/instagram-",
  "/tiktok-",
  "/youtube-",
  "/produto/",
  "/servico/",
  "/produtos",
  "/servicos",
  "/tutoriais",
  "/blog",
  "/sobre",
  "/parceiros",
];

// Routes that are NEVER recorded, regardless of sample / consent.
// Deny ALWAYS wins.
const DENY_PREFIXES = [
  "/admin",
  "/auth",
  "/reset-password",
  "/carteira",
  "/api-keys",
  "/suporte",
  "/comprovante",
  "/meus-pedidos",
  "/meus-servicos",
  "/unsubscribe",
  "/policies",
  "/__obs-debug",
];

// Selectors that the recorder must always BLOCK (not even shape captured).
// Anything else falls back to deny-by-default text masking.
export const REPLAY_BLOCK_SELECTORS = [
  "[data-replay-block]",
  "iframe",
  "input",
  "textarea",
  "select",
  "[contenteditable]",
  "[data-pix]",
  "[data-receipt]",
  "[data-balance]",
  "[data-wallet]",
  "[data-cpf]",
  "[data-card]",
  ".sensitive",
  // shadcn primitives that often render OTP/PIX codes inline
  "[role='textbox']",
].join(", ");

// CSS class hints (used by `ignoreClass` in PostHog).
export const REPLAY_IGNORE_CLASS = "ph-no-capture";
export const REPLAY_MASK_CLASS = "ph-mask";

export function isAllowedRoute(path: string): boolean {
  if (!path) return false;
  for (const deny of DENY_PREFIXES) {
    if (path === deny || path.startsWith(deny + "/") || path.startsWith(deny)) return false;
  }
  if (path === "/") return true;
  return ALLOW_PREFIXES.some((p) => path === p || path.startsWith(p));
}

/**
 * Stable per-session sampling decision: roll once, persist for the tab's
 * lifetime. Avoids the dice flipping between page changes mid-session.
 */
function getSessionRoll(): number {
  try {
    const cached = sessionStorage.getItem(REPLAY.storage_session_decision_key);
    if (cached) return Number(cached);
    const roll = Math.random();
    sessionStorage.setItem(REPLAY.storage_session_decision_key, String(roll));
    return roll;
  } catch {
    return Math.random();
  }
}

export function markErrorOverride(): void {
  try {
    sessionStorage.setItem(REPLAY.storage_error_override_key, "1");
  } catch {
    /* noop */
  }
}

export function hasErrorOverride(): boolean {
  try {
    return sessionStorage.getItem(REPLAY.storage_error_override_key) === "1";
  } catch {
    return false;
  }
}

export interface ReplayDecision {
  shouldRecord: boolean;
  reason: "consent" | "deny_route" | "allow_route" | "sample" | "error_override" | "no_route_match";
}

/**
 * Compute whether the replay recorder should be ACTIVE for a given path,
 * given the current consent state.
 */
export function evaluateReplay(args: {
  path: string;
  consentReplay: boolean;
}): ReplayDecision {
  if (!args.consentReplay) return { shouldRecord: false, reason: "consent" };
  // Deny list is absolute.
  for (const deny of DENY_PREFIXES) {
    if (args.path === deny || args.path.startsWith(deny + "/") || args.path.startsWith(deny)) {
      return { shouldRecord: false, reason: "deny_route" };
    }
  }
  // Error override bypasses sample AND allow list (but never deny).
  if (hasErrorOverride()) return { shouldRecord: true, reason: "error_override" };

  if (!isAllowedRoute(args.path)) return { shouldRecord: false, reason: "no_route_match" };

  const roll = getSessionRoll();
  if (roll > REPLAY.sample_rate) return { shouldRecord: false, reason: "sample" };
  return { shouldRecord: true, reason: "allow_route" };
}

// Re-export so debug surfaces can show the current consent policy version.
export const REPLAY_POLICY_VERSION = CONSENT.policy_version;
